/**
 * Flat-illustration silhouettes for the 2D view — reuses the SAME
 * VesselProfile.radiusAt(t) used to revolve the 3D Lathe geometry
 * (vesselGeometry.ts), just sampled as a flat side silhouette instead of
 * revolved around Y. One profile, two projections — a vessel #5 added to
 * vesselGeometry.ts automatically gets a 2D silhouette too.
 *
 * The viewer stands a little ABOVE the bench rather than level with it (see
 * VIEW_TILT), so every horizontal circle on a vessel — its mouth, its liquid
 * surface, its floor — projects to an ellipse. Because a profile is a solid
 * of revolution, that costs nothing per shape: an ellipse's widest points sit
 * at its own centre height, so the side walls are exactly the silhouette they
 * were before, and only the top and bottom close as arcs instead of as
 * straight lines. Every ellipse in the drawing has to share one eccentricity,
 * though — a mouth seen from above over a liquid surface seen edge-on is the
 * single fastest way to make flat art read as a broken 3D render.
 */
import { VESSEL_PROFILES, fillTForVolumeML, type VesselProfile, type Spout } from './vesselGeometry';
import type { VesselType } from './vesselTypes';
import { VIEW_TILT, ellipsePath, frontArcPath, bandPath } from './projection';

/** Pixels per 3D-profile unit. */
export const PX_SCALE = 110;

/** The floor gets a shallower tilt than the scene's own. It is the circle
 *  furthest below the eye and so geometrically the MOST open, but a beaker's
 *  base is also the circle you see least of — it is pressed against the bench,
 *  and its own corner fillet hides the rest. Drawn in full it reads as a
 *  round-bottomed bowl. */
const BASE_TILT = VIEW_TILT * 0.5;

/** How far the rim stands PROUD of the wall, radially. The band between the
 *  mouth's outer and inner ellipses used to be cut inward — the wall's own
 *  thickness, taken out of the opening — which narrowed the mouth to less
 *  than the vessel. It goes outward instead: the opening is now exactly the
 *  bore, flush with the walls it continues, and the band is the slightly
 *  out-turned bead a real rim carries. Beyond looking right, it is what opens
 *  the mouth up, since the whole width of the vessel is now hole.
 *
 *  A SHARE of the rim, not a fixed 4px. Fixed, it was 8% of a beaker's rim and
 *  28% of a flask's neck — so the same number that read as a lip on the one
 *  read as a collar clamped round the other. Floored, because a bead thinner
 *  than about a pixel and a half stops being drawable at all. */
function rimBeadPx(rimHalfWidthPx: number): number {
  return Math.max(1.6, Math.min(4, rimHalfWidthPx * 0.12));
}

const f = (n: number): string => n.toFixed(1);

/** A raised cosine, 1 at the centre of the spout and 0 at its edges and
 *  beyond, so the bulge blends into the plain rim with no seam or kink. */
function spoutFalloff(theta: number, spout: Spout): number {
  let d = theta - spout.angle;
  d = ((d + Math.PI) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2) - Math.PI;
  const w = Math.min(1, Math.abs(d) / spout.width);
  return w >= 1 ? 0 : Math.cos((w * Math.PI) / 2) ** 3;
}

/** One edge of the rim, all the way round: a circle of `radiusPx` seen from
 *  the scene's eye height, EXCEPT where a spout draws it outward and up.
 *  Sampled rather than arced, because the whole point is that this is not an
 *  ellipse — a spout is a one-sided feature and no arc can carry one.
 *  `scale` is how much of the spout this particular edge takes: the outer edge
 *  takes all of it, the bore less, and the gap between them is the trough. */
function rimCurvePath(cy: number, radiusPx: number, spout: Spout | null, scale = 1, segments = 96): string {
  const pts: string[] = [];
  for (let i = 0; i <= segments; i++) {
    const theta = (i / segments) * Math.PI * 2;
    const g = spout ? spoutFalloff(theta, spout) * scale : 0;
    const r = radiusPx + (spout ? spout.bump * PX_SCALE * g : 0);
    const lift = spout ? spout.lift * PX_SCALE * g : 0;
    pts.push(`${f(r * Math.cos(theta))},${f(cy + r * Math.sin(theta) * VIEW_TILT - lift)}`);
  }
  return `M ${pts[0]} ${pts.slice(1).map(p => `L ${p}`).join(' ')} Z`;
}

export interface Vessel2D {
  /** Floor to the rim's CENTRE, in px — where the mouth's ellipse is centred,
   *  and so where anything positioned "at the rim" belongs. */
  heightPx: number;
  /** Floor to the highest ink the vessel puts on the page: the rim's far edge
   *  stands rimRyPx above its centre. What headroom and zoom have to budget
   *  for, where heightPx is what things are positioned against. */
  overallHeightPx: number;
  /** Half-height of the mouth's ellipse, in px. */
  rimRyPx: number;
  /** The rim itself: an annulus between the mouth's outer and inner ellipses
   *  — the glass's own thickness, seen from above. Fill it with `evenodd`. */
  rimBandPath: string;
  /** The opening inside that band — what you see down through. */
  mouthInnerPath: string;
  /** The mouth's outer edge alone, for a ring drawn around it. */
  rimOuterPath: string;
  /** The liquid's surface at a given fill fraction [0,1]: an ellipse, since
   *  the mouth above it is one. */
  surfacePath(fillT: number): string;
  /** Its near edge alone, for the lifted highlight along the waterline. */
  surfaceFrontArc(fillT: number): string;
  /** Outline polygon (SVG path `d`), local origin at bottom-center (bench line), y grows upward negative. */
  outlinePath: string;
  /** Liquid surface polygon for a given fill fraction [0,1]. */
  liquidPath(fillT: number): string;
  /** Half-width at the very rim, in px — for pH paper/label placement. */
  rimHalfWidthPx: number;
  /** Half-width near the floor, in px — for precipitate width. */
  floorHalfWidthPx: number;
  /** Half-width at the resting liquid level, in px — the meniscus ellipse. */
  liquidHalfWidthPx: number;
  /** Radius the stand should hug, in px (mirrors standRadius in 3D). */
  standRadiusPx: number;
  /** Widest half-width anywhere on the profile, in px — what an upright rod
   *  has to clear. */
  maxHalfWidthPx: number;
  /** Where a clamp grips: half-width and height above the floor, in px. */
  gripHalfWidthPx: number;
  gripYPx: number;
  /** The ground-glass collar near the mouth: half-width and y, in px, plus the
   *  band it draws as and the front edge of its top face. Null for a vessel
   *  that has none. */
  collar: { halfWidthPx: number; y: number; bandPath: string; topFrontArc: string } | null;
  hasFlatBase: boolean;
}

// Uniform sampling, so the segment count is set by the SMALLEST feature on
// any profile rather than by the silhouette as a whole: a base fillet spans
// about 7% of a vessel's height, and at the old 22 segments that is one and a
// half chords — a chamfer, not a round.
const OUTLINE_SEGMENTS = 96;

/** How far the wall is drawn out at height `t` by a spout above it, in px.
 *  Smoothstep, so the bulge leaves the vertical with ZERO slope at the bottom
 *  of the blend — the wall bends away from straight rather than turning a
 *  corner into the beak, which is the whole point of the spout reaching below
 *  the lip at all. */
function spoutWallOffsetPx(profile: VesselProfile, t: number): number {
  const spout = profile.spout;
  if (!spout || t <= 1 - spout.depth) return 0;
  const u = (t - (1 - spout.depth)) / spout.depth;
  return spout.bump * PX_SCALE * (u * u * (3 - 2 * u));
}

function outline(profile: VesselProfile, minT: number, maxT: number, segments = OUTLINE_SEGMENTS): string {
  const right: [number, number][] = [];
  const left: [number, number][] = [];
  for (let i = 0; i <= segments; i++) {
    const t = minT + ((maxT - minT) * i) / segments;
    const x = profile.radiusAt(t) * PX_SCALE;
    const y = -t * profile.height * PX_SCALE; // up is negative in this local space
    right.push([x, y]);
    // Only the left wall: a spout is one-sided, and this one is on the left
    // (see the beaker's Spout.angle). The two sides of the silhouette stop
    // being mirror images of each other here, which is the price of drawing
    // a feature a body of revolution does not have.
    left.push([-(x + spoutWallOffsetPx(profile, t)), y]);
  }
  left.reverse();
  const topR = right[right.length - 1][0], topY = right[right.length - 1][1];
  const topLeftR = -left[0][0];
  const baseR = right[0][0];
  const wall = (pts: [number, number][]) => pts.slice(1).map(([x, y]) => `L ${f(x)},${f(y)}`).join(' ');
  // Up the right wall, over the FAR half of the top circle, down the left
  // wall, and back under the near half of the base. A vessel that closes to a
  // pole at the bottom (round flask, test tube) has no base circle to draw.
  const base = baseR < 0.5 ? '' : ` A ${f(baseR)},${f(baseR * BASE_TILT)} 0 0 0 ${f(baseR)},${f(right[0][1])}`;
  return `M ${f(right[0][0])},${f(right[0][1])} ${wall(right)}`
    + ` A ${f(topR)},${f(topR * VIEW_TILT)} 0 0 0 ${f(-topLeftR)},${f(topY)} `
    + `${wall(left)}${base} Z`;
}

function build(type: VesselType): Vessel2D {
  const profile = VESSEL_PROFILES[type];
  const rimHalfWidthPx = profile.radiusAt(1) * PX_SCALE;
  const rimYPx = -profile.height * PX_SCALE;
  const rimOuterHalfWidthPx = rimHalfWidthPx + rimBeadPx(rimHalfWidthPx);
  const rimRyPx = rimOuterHalfWidthPx * VIEW_TILT;
  // A spout is glass like any other: it takes width on the bench and it stands
  // above the rim's centre, so neither the column that reserves room for this
  // vessel nor the shelf above it may ignore it.
  const spoutReachPx = profile.spout ? profile.spout.bump * PX_SCALE : 0;
  const spoutLiftPx = profile.spout ? profile.spout.lift * PX_SCALE : 0;
  // Flush with the walls: the mouth is the bore, not the bore less the glass.
  const innerR = rimHalfWidthPx;
  const surfaceRx = (fillT: number) => profile.radiusAt(Math.min(fillT, 1)) * PX_SCALE;
  const surfaceCy = (fillT: number) => -Math.min(fillT, 1) * profile.height * PX_SCALE;
  return {
    heightPx: profile.height * PX_SCALE,
    overallHeightPx: profile.height * PX_SCALE + Math.max(rimRyPx, spoutLiftPx),
    rimRyPx,
    rimBandPath: `${rimCurvePath(rimYPx, rimOuterHalfWidthPx, profile.spout)} ${rimCurvePath(rimYPx, innerR, profile.spout, profile.spout?.innerScale ?? 1)}`,
    mouthInnerPath: rimCurvePath(rimYPx, innerR, profile.spout, profile.spout?.innerScale ?? 1),
    rimOuterPath: rimCurvePath(rimYPx, rimOuterHalfWidthPx, profile.spout),
    surfacePath: (fillT: number) => ellipsePath(surfaceCy(fillT), surfaceRx(fillT), surfaceRx(fillT) * VIEW_TILT),
    surfaceFrontArc: (fillT: number) => frontArcPath(surfaceCy(fillT), surfaceRx(fillT), surfaceRx(fillT) * VIEW_TILT),
    outlinePath: outline(profile, 0, 1),
    liquidPath: (fillT: number) => outline(profile, 0, Math.min(fillT, 1)),
    rimHalfWidthPx,
    floorHalfWidthPx: profile.radiusAt(0.04) * PX_SCALE,
    liquidHalfWidthPx: profile.radiusAt(profile.fillT) * PX_SCALE,
    standRadiusPx: profile.standRadius * PX_SCALE,
    maxHalfWidthPx: Math.max(profile.maxRadius * PX_SCALE, rimOuterHalfWidthPx + spoutReachPx),
    gripHalfWidthPx: profile.radiusAt(profile.gripT) * PX_SCALE,
    gripYPx: -profile.gripT * profile.height * PX_SCALE,
    collar: profile.collarT === null ? null : (() => {
      const halfWidthPx = profile.radiusAt(profile.collarT) * PX_SCALE;
      const y = -profile.collarT * profile.height * PX_SCALE;
      // Proud of the neck by a share of it, for the same reason the bead is.
      const rx = halfWidthPx * 1.18, ry = rx * VIEW_TILT;
      return {
        halfWidthPx, y,
        bandPath: bandPath(y - 5, y + 5, rx, ry),
        topFrontArc: frontArcPath(y - 5, rx, ry),
      };
    })(),
    hasFlatBase: profile.hasFlatBase,
  };
}

export const VESSEL_2D: Record<VesselType, Vessel2D> = {
  flask: build('flask'),
  beaker: build('beaker'),
  'round-flask': build('round-flask'),
  'test-tube': build('test-tube'),
};

/** Where the surface of `volumeML` stands in this vessel, in local pixels.
 *  Not a share of the height: the same volume is a shallow disc in a beaker
 *  and a deep column in a test tube, which is what fillTForVolumeML works out
 *  from the vessel's own profile. */
export function liquidTopYPx(type: VesselType, volumeML: number): number {
  const profile = VESSEL_PROFILES[type];
  return -fillTForVolumeML(type, volumeML) * profile.height * PX_SCALE;
}

export function rimYPx(type: VesselType): number {
  return -VESSEL_2D[type].heightPx;
}
